import * as React from "react";

import { useLoginForm } from "../login-form-provider";
import {
  PRESENTATION_SCROLL_ACTIONS,
  usePresenterForScroll_asController,
} from "../presenter-for-scroll-provider";
import { useProminentError } from "../prominent-error-provider";

import {
  useOneTimePasswordSendCodeForm,
  useOneTimePasswordSendCodeForm_asController,
} from "./one-time-password-send-code-form-provider";
import { useOneTimePasswordVerifyCodeForm_asController } from "./one-time-password-verify-code-form-provider";

interface OneTimePasswordUserExperienceConsumerInterface {
  presentingUserExperienceStep: OneTimePasswordUserExperience_Step;
  showBackButton?: boolean;
  handleBack: () => void;
  handleClose: () => void;
}
const defaultValue =
  {} as unknown as OneTimePasswordUserExperienceConsumerInterface;
const OneTimePasswordUserExperienceContext = React.createContext(defaultValue);

interface OneTimePasswordUserExperience_ControllerConsumerInterface {
  handleStartTheUserFlowForAOneTimeCodeBasedLogin: () => void;
  handleStartTheVerifyCodeStep: () => void;
}
const defaultValue_controller =
  {} as unknown as OneTimePasswordUserExperience_ControllerConsumerInterface;
const OneTimePasswordUserExperience_ControllerContext = React.createContext(
  defaultValue_controller
);

export enum OneTimePasswordUserExperience_Step {
  NOT_PRESENTING = "NOT_PRESENTING",
  FIRST_STEP_SEND_CODE = "FIRST_STEP_SEND_CODE",
  SECOND_STEP_VERIFY_CODE = "SECOND_STEP_VERIFY_CODE",
}

export const OneTimePasswordUserExperienceProvider: React.FunctionComponent = (
  props
) => {
  const { form: loginForm } = useLoginForm();
  const { onFormInputChange: handleSendCodeFormInputChange } =
    useOneTimePasswordSendCodeForm();
  const { resetForm: resetSendCodeForm } =
    useOneTimePasswordSendCodeForm_asController();
  const { resetForm: resetVerifyCodeForm } =
    useOneTimePasswordVerifyCodeForm_asController();
  const { setProminentError } = useProminentError();

  const { initiateScrollAction } = usePresenterForScroll_asController();

  const [presentingUserExperienceStep, setPresentingUserExperienceStep] =
    React.useState<OneTimePasswordUserExperience_Step>(
      OneTimePasswordUserExperience_Step.NOT_PRESENTING
    );

  const showBackButton = React.useMemo(() => {
    let showBackButton = false;
    switch (presentingUserExperienceStep) {
      case OneTimePasswordUserExperience_Step.SECOND_STEP_VERIFY_CODE:
        showBackButton = true;
        break;
    }
    return showBackButton;
  }, [presentingUserExperienceStep]);

  const handleBack = React.useCallback(() => {
    switch (presentingUserExperienceStep) {
      case OneTimePasswordUserExperience_Step.SECOND_STEP_VERIFY_CODE:
        setProminentError(null);
        resetVerifyCodeForm();
        setPresentingUserExperienceStep(
          OneTimePasswordUserExperience_Step.FIRST_STEP_SEND_CODE
        );
        break;
    }
  }, [setProminentError, resetVerifyCodeForm, presentingUserExperienceStep]);

  const handleClose = React.useCallback(() => {
    setProminentError(null);
    setPresentingUserExperienceStep(
      OneTimePasswordUserExperience_Step.NOT_PRESENTING
    );
    resetSendCodeForm();
    resetVerifyCodeForm();
  }, [
    setProminentError,
    resetSendCodeForm,
    resetVerifyCodeForm,
    setPresentingUserExperienceStep,
  ]);

  const handleStartTheUserFlowForAOneTimeCodeBasedLogin: OneTimePasswordUserExperience_ControllerConsumerInterface["handleStartTheUserFlowForAOneTimeCodeBasedLogin"] =
    React.useCallback(() => {
      setProminentError(null);
      resetSendCodeForm();
      resetVerifyCodeForm();
      setPresentingUserExperienceStep(
        OneTimePasswordUserExperience_Step.FIRST_STEP_SEND_CODE
      );
      handleSendCodeFormInputChange("email", loginForm.email || "");
      initiateScrollAction(PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_TOP);
    }, [
      setProminentError,
      resetSendCodeForm,
      resetVerifyCodeForm,
      setPresentingUserExperienceStep,
      initiateScrollAction,
      handleSendCodeFormInputChange,
      loginForm.email,
    ]);

  const handleStartTheVerifyCodeStep: OneTimePasswordUserExperience_ControllerConsumerInterface["handleStartTheVerifyCodeStep"] =
    React.useCallback(() => {
      setProminentError(null);
      setPresentingUserExperienceStep(
        OneTimePasswordUserExperience_Step.SECOND_STEP_VERIFY_CODE
      );
      initiateScrollAction(PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_TOP);
    }, [
      setProminentError,
      setPresentingUserExperienceStep,
      initiateScrollAction,
    ]);

  const value: OneTimePasswordUserExperienceConsumerInterface = React.useMemo(
    () => ({
      presentingUserExperienceStep,
      showBackButton,
      handleBack,
      handleClose,
    }),
    [presentingUserExperienceStep, showBackButton, handleBack]
  );
  const controllerValue: OneTimePasswordUserExperience_ControllerConsumerInterface =
    React.useMemo(
      () => ({
        handleStartTheUserFlowForAOneTimeCodeBasedLogin,
        handleStartTheVerifyCodeStep,
      }),
      [
        handleStartTheUserFlowForAOneTimeCodeBasedLogin,
        handleStartTheVerifyCodeStep,
      ]
    );
  return (
    <OneTimePasswordUserExperienceContext.Provider value={value}>
      <OneTimePasswordUserExperience_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </OneTimePasswordUserExperience_ControllerContext.Provider>
    </OneTimePasswordUserExperienceContext.Provider>
  );
};

export const useOneTimePasswordUserExperience =
  (): OneTimePasswordUserExperienceConsumerInterface => {
    const context = React.useContext(OneTimePasswordUserExperienceContext);
    if (context === defaultValue) {
      throw new Error(
        "useOneTimePasswordUserExperience must be used within a OneTimePasswordUserExperienceProvider"
      );
    }
    return context;
  };

export const useOneTimePasswordUserExperience_asController =
  (): OneTimePasswordUserExperience_ControllerConsumerInterface => {
    const context = React.useContext(
      OneTimePasswordUserExperience_ControllerContext
    );
    if (context === defaultValue_controller) {
      throw new Error(
        "useOneTimePasswordUserExperience_asController must be used within a OneTimePasswordUserExperienceProvider"
      );
    }
    return context;
  };
